import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Lock, Mail, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, setSession, getUser } from "@/lib/api";
import { track } from "@/lib/analytics";
import { useSeo } from "@/lib/useSeo";
import { Logo } from "@/components/SiteChrome";

const inputCls = "w-full rounded-md border border-zinc-800 bg-zinc-950 px-4 py-3 text-sm outline-none focus:border-[#A855F7] focus:ring-1 focus:ring-[#A855F7]";
const btnCls = "mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#A855F7] px-5 py-3 text-sm font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60";

export default function Login() {
  useSeo({
    title: "Ambassador Login — NoobSync Brand Ambassador",
    description: "Sign in to your NoobSync Brand Ambassador dashboard with your password or a one-time email code. Track your points, streak and referral tools.",
    path: "/login",
  });
  const nav = useNavigate();
  // Already signed in? Skip the login form and go straight to the right home.
  useEffect(() => {
    const u = getUser();
    if (u) nav(u.role === "admin" ? "/admin" : u.content_manager ? "/content-manager" : "/dashboard", { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [mode, setMode] = useState("password"); // password | otp
  const [phase, setPhase] = useState("login"); // login | otp-sent | reset-sent | setpw
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [setPwToken, setSetPwToken] = useState(null);
  const [loading, setLoading] = useState(false);

  const routeAfterAuth = (data) => {
    setSession(data);
    track("login_success", { role: data.user.role });
    const u = data.user;
    nav(u.role === "admin" ? "/admin" : u.content_manager ? "/content-manager" : "/dashboard");
  };
  const err = (e, fallback) => toast.error(e?.response?.data?.detail || fallback);

  const passwordLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/login", { email: email.trim().toLowerCase(), password });
      routeAfterAuth(r.data);
    } catch (e2) {
      if (e2?.response?.status === 409) { toast.info("Let's set your password. Sending you an email code…"); setMode("otp"); await sendOtp(); }
      else err(e2, "Sign in failed");
    } finally { setLoading(false); }
  };

  const sendOtp = async () => {
    setLoading(true);
    try {
      await api.post("/auth/request-otp", { email: email.trim().toLowerCase(), purpose: "login" });
      setPhase("otp-sent");
    } catch (e2) { err(e2, "Could not send code"); } finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/verify-otp", { email: email.trim().toLowerCase(), code: code.trim() });
      if (r.data.must_set_password) { setSetPwToken(r.data.set_token); setPhase("setpw"); toast.info("Create a password to finish setting up your account."); }
      else routeAfterAuth(r.data);
    } catch (e2) { err(e2, "Invalid code"); } finally { setLoading(false); }
  };

  const requestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/auth/request-password-reset", { email: email.trim().toLowerCase() }); setPhase("reset-sent"); }
    catch (e2) { err(e2, "Could not send code"); } finally { setLoading(false); }
  };

  const verifyReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/verify-reset", { email: email.trim().toLowerCase(), code: code.trim() });
      setSetPwToken(r.data.set_token); setPhase("setpw");
    } catch (e2) { err(e2, "Invalid code"); } finally { setLoading(false); }
  };

  const submitSetPw = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      const r = await api.post("/auth/set-password", { set_token: setPwToken, new_password: newPw });
      toast.success("Password set — you're in!");
      routeAfterAuth(r.data);
    } catch (e2) { err(e2, "Could not set password"); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-transparent text-white">
      <header className="border-b border-zinc-900">
        <div className="mx-auto max-w-3xl px-5 py-5"><Logo /></div>
      </header>
      <div className="mx-auto flex max-w-md flex-col items-center px-5 py-16 text-center">
        <Lock className="h-10 w-10 text-[#A855F7]" />
        <h1 className="mt-4 font-display text-3xl font-black tracking-tighter">Sign in</h1>

        {/* SET PASSWORD (legacy first-login or reset) */}
        {phase === "setpw" ? (
          <form onSubmit={submitSetPw} className="mt-8 w-full text-left" data-testid="setpw-form">
            <p className="mb-4 text-center text-sm text-zinc-400">Create a password for <span className="font-mono text-[#A855F7]">{email}</span>. Min 8 chars, with a letter and a number.</p>
            <input data-testid="setpw-new" type="password" required value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="New password" className={inputCls} />
            <input data-testid="setpw-confirm" type="password" required value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} placeholder="Confirm password" className={`${inputCls} mt-3`} />
            <button data-testid="setpw-submit" disabled={loading} className={btnCls}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Set password & sign in</button>
          </form>
        ) : phase === "otp-sent" || phase === "reset-sent" ? (
          <form onSubmit={phase === "reset-sent" ? verifyReset : verifyOtp} className="mt-8 w-full text-left" data-testid="otp-code-form">
            <p className="mb-4 text-center text-sm text-zinc-400">We emailed a 6-digit code to <span className="font-mono text-[#A855F7]">{email}</span>. It expires in 10 minutes.</p>
            <input data-testid="otp-code-input" inputMode="numeric" autoFocus required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="Enter code" className={`${inputCls} text-center text-lg tracking-[0.4em]`} />
            <button data-testid="otp-verify-btn" disabled={loading} className={btnCls}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Verify code</button>
            <button type="button" onClick={() => { setPhase("login"); setCode(""); }} className="mt-4 block w-full text-xs text-zinc-400 hover:text-white">← Use a different email</button>
          </form>
        ) : (
          <>
            <div className="mt-6 flex w-full rounded-lg border border-zinc-800 p-1 text-sm">
              <button data-testid="tab-password" onClick={() => setMode("password")} className={`flex-1 rounded-md py-2 font-semibold ${mode === "password" ? "bg-[#A855F7] text-white" : "text-zinc-400"}`}>Password</button>
              <button data-testid="tab-otp" onClick={() => setMode("otp")} className={`flex-1 rounded-md py-2 font-semibold ${mode === "otp" ? "bg-[#A855F7] text-white" : "text-zinc-400"}`}>Email code</button>
            </div>

            {mode === "password" ? (
              <form onSubmit={passwordLogin} className="mt-6 w-full text-left" data-testid="password-form">
                <input data-testid="login-email-input" type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className={inputCls} />
                <input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={`${inputCls} mt-3`} />
                <button data-testid="login-submit-btn" disabled={loading} className={btnCls}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Sign in</button>
                <button type="button" data-testid="forgot-password-btn" onClick={(e) => { if (!email.trim()) { toast.error("Enter your email first"); return; } requestReset(e); }} className="mt-4 block w-full text-xs text-zinc-400 hover:text-white">Forgot password?</button>
              </form>
            ) : (
              <form onSubmit={(e) => { e.preventDefault(); if (!email.trim()) return toast.error("Enter your email"); sendOtp(); }} className="mt-6 w-full text-left" data-testid="otp-request-form">
                <p className="mb-3 text-center text-sm text-zinc-400"><Mail className="mr-1 inline h-4 w-4" /> We'll email you a one-time code — no password needed.</p>
                <input data-testid="otp-email-input" type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className={inputCls} />
                <button data-testid="otp-send-btn" disabled={loading} className={btnCls}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Email me a code</button>
              </form>
            )}
          </>
        )}

        <p className="mt-8 text-sm text-zinc-500">New here? <Link to="/apply" className="font-semibold text-[#A855F7] hover:underline">Apply to join</Link></p>
        <Link to="/" className="mt-4 text-sm text-zinc-500 hover:text-white">← Back to home</Link>
      </div>
    </div>
  );
}

// Backward-compat: existing magic links still work.
export function VerifyMagicLink() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [status, setStatus] = useState("verifying");
  useEffect(() => {
    const token = params.get("token");
    if (!token) { setStatus("error"); return; }
    api.post("/auth/verify", { token })
      .then((r) => {
        setSession(r.data);
        if (r.data.user.role === "admin") nav("/admin");
        else if (r.data.user.content_manager) nav("/content-manager");
        else nav("/dashboard");
      })
      .catch(() => setStatus("error"));
  }, [params, nav]);
  return (
    <div className="min-h-screen bg-transparent text-white">
      <div className="mx-auto flex max-w-md flex-col items-center px-5 py-32 text-center">
        <Logo />
        {status === "verifying" ? (
          <><Loader2 className="mt-12 h-8 w-8 animate-spin text-[#A855F7]" /><p className="mt-4 text-zinc-400">Signing you in…</p></>
        ) : (
          <div className="mt-12">
            <p className="font-display text-2xl font-bold">Link invalid or expired</p>
            <Link to="/login" data-testid="verify-retry" className="mt-6 inline-flex rounded-md bg-[#A855F7] px-5 py-2.5 text-sm font-bold text-white">Go to sign in</Link>
          </div>
        )}
      </div>
    </div>
  );
}
